import axios from 'axios';
import { postData } from './axiosUtils';

const buildFormData = (entity: {
  id?: string;
  name: string;
  htmlContent?: string;
  file?: File | null;
  responsibilityAccepted: boolean;
}) => {
  const formData = new FormData();
  if (entity.id) {
    formData.append('id', entity.id);
  }
  formData.append('name', entity.name ?? '');
  if (entity.htmlContent != null && entity.htmlContent !== '') {
    formData.append('htmlContent', entity.htmlContent);
  }
  if (entity.file) {
    formData.append('file', entity.file);
  }
  formData.append(
    'responsibilityAccepted',
    String(!!entity.responsibilityAccepted)
  );
  return formData;
};

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const parseFileName = (contentDisposition: string | undefined, fallback: string) => {
  if (!contentDisposition) return fallback;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plainMatch?.[1] ?? fallback;
};

const htmlAppletService = {
  list: () => {
    return axios.get(`/api/HtmlApplet/list`).then((response) => {
      return response.data;
    });
  },
  get: (id: any) => {
    return axios.get(`/api/HtmlApplet/details/${id}`).then((response) => {
      return response.data;
    });
  },
  create: (entity: any) => {
    return axios
      .post(`/api/HtmlApplet/create`, buildFormData(entity), {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => response.data);
  },
  update: (entity: any) => {
    return axios
      .put(`/api/HtmlApplet/update`, buildFormData(entity), {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => response.data);
  },
  delete: (id: any) => {
    return postData({
      url: `/api/HtmlApplet/delete/${id}`,
      method: 'DELETE',
    }).then((response) => {
      return response;
    });
  },
  listResults: (
    appletId?: string | null,
    options?: { page?: number; pageSize?: number }
  ) => {
    const params = new URLSearchParams();
    if (appletId) params.set('appletId', appletId);
    params.set('page', String(options?.page ?? 1));
    params.set('pageSize', String(options?.pageSize ?? 50));
    return axios
      .get(`/api/HtmlApplet/listResults?${params.toString()}`)
      .then((response) => {
        return response.data;
      });
  },
  /**
   * Downloads applet results as jsonl (raw) or xlsx (flattened columns).
   */
  exportResults: async (
    appletId: string,
    format: 'jsonl' | 'xlsx'
  ): Promise<void> => {
    const response = await axios.get(`/api/HtmlApplet/exportResults`, {
      params: { appletId, format },
      responseType: 'blob',
    });

    const contentType = String(response.headers['content-type'] ?? '');
    if (contentType.includes('application/json')) {
      const text = await (response.data as Blob).text();
      let message = 'Export sikertelen';
      try {
        const parsed = JSON.parse(text);
        message = (parsed.errorMessages ?? []).join(',') || message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }

    const fallback =
      format === 'jsonl'
        ? `html-applet-results.jsonl`
        : `html-applet-results.xlsx`;
    const fileName = parseFileName(
      response.headers['content-disposition'],
      fallback
    );
    triggerBrowserDownload(response.data as Blob, fileName);
  },
};

export default htmlAppletService;
